import {
  del,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  set,
} from 'vue';

import { VALIDATION_INJECTION_KEY } from './useFormValidator';

import type { ValidationInjection } from './useFormValidator';

export type FormFieldRules = Record<string, unknown>;

export interface UseFormFieldOptions {
  field: () => string;
  initialValue: () => unknown;
  validators: () => FormFieldRules;
  touchOnMountIfInitial?: boolean;
}

export interface UseFormFieldReturn {
  validation: ValidationInjection;
  setValidators: () => void;
  deleteValidators: () => void;
  updateValidators: () => void;
  clearServerError: () => void;
}

export function useFormField (options: UseFormFieldOptions): UseFormFieldReturn {
  const injectedValidation = inject(VALIDATION_INJECTION_KEY);

  if (!injectedValidation) {
    throw new Error('useFormField: не найден провайдер валидации (useFormValidator)');
  }

  const validation: ValidationInjection = injectedValidation;

  function setValidators (): void {
    const field = options.field();

    set(validation.validators.form, field, {
      ...options.validators(),
      serverError: (): boolean => !validation.serverErrors[field],
    });
  }

  function deleteValidators (): void {
    del(validation.validators.form, options.field());
  }

  function updateValidators (): void {
    deleteValidators();
    setValidators();
  }

  function clearServerError (): void {
    const field = options.field();

    if (validation.serverErrors[field]) {
      del(validation.serverErrors, field);
    }
  }

  setValidators();
  set(validation.form, options.field(), options.initialValue());

  if (options.touchOnMountIfInitial) {
    onMounted(() => {
      if (!options.initialValue()) {
        return;
      }

      nextTick(() => {
        validation.$v[options.field()].$touch();
      });
    });
  }

  onBeforeUnmount(() => {
    deleteValidators();
    del(validation.form, options.field());
  });

  return {
    validation,
    setValidators,
    deleteValidators,
    updateValidators,
    clearServerError,
  };
}
